import { MonthPicker } from '@/components/layout/MonthPicker';
import { PageHeader } from '@/components/layout/PageHeader';
import { EntityLink, NavAction } from '@/components/nav/EntityLink';
import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { Meter } from '@/components/ui/meter';
import { Stat, StatRow } from '@/components/ui/stat';
import { Table, TableWrap, Td, TdNum, Th } from '@/components/ui/table';
import type { UtilizationReport } from '@/lib/api';
import { hours, percent } from '@/lib/format';
import { toAssignments, toMember, useMonthParam } from '@/lib/nav';
import { useApi } from '@/lib/useApi';

/** アサイン・稼働管理 */
export function UtilizationPage() {
  const [month, setMonth] = useMonthParam();
  const { data, error, loading } = useApi<UtilizationReport>(
    `/api/reports/utilization?month=${month}`,
  );

  const totals = data?.totals;
  const directRatio =
    totals && totals.directHours + totals.indirectHours > 0
      ? totals.directHours / (totals.directHours + totals.indirectHours)
      : 0;

  return (
    <>
      <PageHeader
        title="アサイン・稼働管理"
        subtitle={data ? `メンバー ${data.members.length} 名` : undefined}
        actions={
          <>
            <MonthPicker month={month} onChange={setMonth} />
            <a
              href={`/api/reports/utilization.csv?month=${month}`}
              className={buttonVariants({ variant: 'primary' })}
            >
              CSV出力
            </a>
          </>
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        <StateBlock loading={loading} error={error} empty={data?.members.length === 0}>
          {totals ? (
            <StatRow className="grid-cols-1 [&>*+*]:border-t md:grid-cols-4 md:[&>*+*]:border-t-0">
              <Stat label="アサイン予定合計" value={hours(totals.plannedHours)} />
              <Stat label="実績合計" value={hours(totals.actualHours)} tone="brand" />
              <Stat label="平均消化率" value={percent(totals.rate)} />
              <Stat
                label="超過・超過見込み"
                value={`${totals.overCount} 名`}
                tone={totals.overCount > 0 ? 'alert' : 'default'}
              />
            </StatRow>
          ) : null}

          <div className="border border-line bg-surface-raised">
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>MEMBER</Th>
                    <Th className="md:w-[38%]">PLANNED / ACTUAL</Th>
                    <Th className="text-right">HOURS</Th>
                    <Th className="text-right">RATE</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {data?.members.map((member) => {
                    const ratio =
                      member.plannedHours > 0
                        ? Math.min(1, member.actualHours / member.plannedHours)
                        : 0;
                    return (
                      <tr key={member.userId}>
                        <Td>
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {/* 単価と権限はメンバー管理でしか触れない (管理者のみ) */}
                              <EntityLink
                                target={toMember({ id: member.userId, name: member.name })}
                                className="font-medium"
                              >
                                {member.name}
                              </EntityLink>
                              {member.over ? <Badge variant="alert">OVER</Badge> : null}
                            </div>
                            <span className="text-xs text-ink-subtle">
                              直接 {hours(member.directHours)} / 間接 {hours(member.indirectHours)}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <Meter
                            segments={[
                              {
                                ratio,
                                className: member.over ? 'bg-alert' : 'bg-brand-500',
                                label: '実績',
                              },
                            ]}
                          />
                        </Td>
                        <TdNum>
                          {member.actualHours} / {member.plannedHours} h
                        </TdNum>
                        <TdNum className={member.over ? 'text-alert' : undefined}>
                          {percent(member.rate)}
                        </TdNum>
                        <Td className="text-right">
                          {/* 超過が見えたら次に見るのは「何にどれだけ積んだか」 */}
                          <NavAction target={toAssignments({ month, query: member.name })} />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </TableWrap>

            <div className="flex flex-wrap items-center gap-5 border-t border-line px-4 py-3 text-[11px] text-ink-faint">
              <span className="flex items-center gap-2">
                <span className="block h-2 w-4.5 bg-brand-500" />
                実績（予定内）
              </span>
              <span className="flex items-center gap-2">
                <span className="block h-2 w-4.5 bg-alert" />
                予定超過
              </span>
            </div>
          </div>

          {totals ? (
            <div className="flex flex-col gap-3 border border-line bg-surface-raised px-6 py-5">
              <div className="flex items-baseline justify-between">
                <span className="text-sm font-bold">直接 / 間接の比率</span>
                <span className="font-mono text-[11px] tracking-wider text-ink-subtle">
                  DIRECT / INDIRECT
                </span>
              </div>
              <Meter
                segments={[
                  { ratio: directRatio, className: 'bg-brand-500', label: '直接' },
                  { ratio: 1 - directRatio, className: 'bg-line-bold', label: '間接' },
                ]}
              />
              <div className="flex gap-6 font-mono text-xs text-ink-muted">
                <span>直接 {hours(totals.directHours)}</span>
                <span>間接 {hours(totals.indirectHours)}</span>
                <span>{percent(directRatio * 100)}</span>
              </div>
            </div>
          ) : null}
        </StateBlock>
      </div>
    </>
  );
}
